import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { createUser, deleteUser, fetchUsers, updateUser, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Plus, Trash2 } from "lucide-react";

const ROLES = [
  { key: "super_admin", labelKey: "superAdmin" },
  { key: "editor", labelKey: "editor" },
];

const emptyForm = { email: "", name: "", password: "", role: "editor", is_active: true, send_invite: true };

export default function UsersManager() {
  const { lang, t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const reload = async () => {
    setLoading(true);
    try { setUsers(await fetchUsers()); }
    catch (e) { toast.error(getErrorMessage(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, password: "", role: u.role, is_active: u.is_active, send_invite: false });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { email: form.email, name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await updateUser(editing.id, payload);
        toast.success(t.itemUpdated);
      } else {
        const payload = {
          email: form.email,
          name: form.name,
          role: form.role,
          is_active: form.is_active,
          send_invite: !form.password && form.send_invite,
        };
        if (form.password) payload.password = form.password;
        const created = await createUser(payload);
        if (created?.invite_sent) toast.success(t.inviteSent);
        else if (payload.send_invite && !created?.email_configured) toast.warning(t.inviteQueuedNoKey);
        else toast.success(t.itemAdded);
      }
      setDialogOpen(false);
      reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = async (u) => {
    if (u.id === me?.id) { toast.error("Cannot delete your own account"); return; }
    if (!window.confirm(t.confirmDelete)) return;
    try { await deleteUser(u.id); toast.success(t.itemDeleted); reload(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const roleLabel = (r) => t[ROLES.find((x) => x.key === r)?.labelKey] || r;

  return (
    <div data-testid="users-panel">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#5C6B6D]">{users.length} {t.tabUsers.toLowerCase()}</p>
        <Button onClick={openNew} data-testid="add-user-btn"
          className="rounded-full bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7] uppercase tracking-widest text-xs h-11 px-5">
          <Plus className="w-4 h-4 me-2" /> {t.addUser}
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-black/5 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-[#F4F2EB] text-xs uppercase tracking-widest text-[#5C6B6D]">
          <div className="col-span-3">{t.userName}</div>
          <div className="col-span-3">{t.email}</div>
          <div className="col-span-2">{t.role}</div>
          <div className="col-span-2">{t.status}</div>
          <div className="col-span-2 text-end">{t.actions}</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#5C6B6D]">…</div>
        ) : (
          users.map((u) => (
            <div key={u.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-t border-black/5 items-center text-sm"
                 data-testid={`user-row-${u.id}`}>
              <div className="col-span-3 font-medium">{u.name}{u.id === me?.id && <span className="text-[10px] text-[#0F5F63] ms-2">({lang === "ar" ? "أنت" : "you"})</span>}</div>
              <div className="col-span-3 text-[#5C6B6D]">{u.email}</div>
              <div className="col-span-2">
                <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                  u.role === "super_admin"
                    ? "bg-[#0F5F63] text-[#FDFBF7]"
                    : "bg-[#F4F2EB] text-[#0F5F63]"
                }`}>
                  {roleLabel(u.role)}
                </span>
              </div>
              <div className="col-span-2">
                <span className={`text-xs ${u.is_active ? "text-[#0F5F63]" : "text-[#E27B58]"}`}>
                  {u.is_active ? t.active : t.inactive}
                </span>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                  className="rounded-full text-[#0F5F63] hover:bg-[#F4F2EB]"><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(u)} data-testid={`delete-user-${u.id}`}
                  disabled={u.id === me?.id}
                  className="rounded-full text-[#E27B58] hover:bg-[#E27B58]/10 disabled:opacity-30"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#FDFBF7] rounded-2xl" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className={`${lang === "ar" ? "font-serif-ar" : "font-serif-en"} text-2xl text-[#0F5F63]`}>
              {editing ? t.editUser : t.addUser}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="uname">{t.userName}</Label>
              <Input id="uname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div>
              <Label htmlFor="uemail">{t.email}</Label>
              <Input id="uemail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
            </div>
            <div>
              <Label htmlFor="upw">
                {t.password}{" "}
                {editing ? (
                  <span className="text-xs text-[#5C6B6D]">— {lang === "ar" ? "اترك فارغًا لعدم التغيير" : "leave empty to keep"}</span>
                ) : (
                  <span className="text-xs text-[#5C6B6D]">— {t.passwordOrInviteHint}</span>
                )}
              </Label>
              <Input id="upw" type="password" minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password"
                placeholder={editing ? "" : t.leaveBlankToInvite}
              />
            </div>
            {!editing && !form.password && (
              <div className="flex items-start gap-3 rounded-xl bg-[#F4F2EB] px-4 py-3" data-testid="user-invite-hint">
                <Switch id="uinvite" checked={form.send_invite} onCheckedChange={(v) => setForm({ ...form, send_invite: v })} data-testid="user-invite-switch" />
                <div>
                  <Label htmlFor="uinvite" className="cursor-pointer">{t.sendInviteEmail}</Label>
                  <p className="text-[10px] text-[#5C6B6D] mt-1">{t.sendInviteHint}</p>
                </div>
              </div>
            )}
            <div>
              <Label>{t.role}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.key} value={r.key} data-testid={`user-role-opt-${r.key}`}>{t[r.labelKey]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="uactive" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="user-active-switch" />
              <Label htmlFor="uactive" className="cursor-pointer">{form.is_active ? t.active : t.inactive}</Label>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t.cancel}</Button>
              <Button type="submit" className="bg-[#0F5F63] hover:bg-[#0A4548] text-[#FDFBF7]" data-testid="user-save-btn">{t.save}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
